import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaUser, FaSignOutAlt, FaHome, FaBook, FaUsers, FaGraduationCap, FaBriefcase, FaTrophy, FaQuestionCircle, FaEnvelope, FaUserCog, FaBrain, FaInbox } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useMessaging } from '../contexts/MessagingContext';
import SEO from './SEO';
import './Navbar.css';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, logout, loading } = useAuth();
  const { unreadCount } = useMessaging();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.nav-menu') && !event.target.closest('.menu-icon')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <SEO 
        title="Navigation - Student Helper"
        description="Access all features of Student Helper through our intuitive navigation menu."
        keywords="student helper navigation, menu, user menu, navigation bar"
      />
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            <div className="menu-icon" onClick={toggleMenu}>
              {isOpen ? <FaTimes /> : <FaBars />}
            </div>
            <Link to="/" className="navbar-logo" onClick={closeMenu}>
              <img src={logo} alt="Union Logo" className="navbar-logo-image" />
              <span className="logo-text">Student Helper</span>
            </Link>
          </div>

          <ul className={`nav-menu ${isOpen ? 'active' : ''}`}>
            <div className="nav-menu-items">
              <li className="nav-item">
                <Link 
                  to="/" 
                  className={`nav-link ${isActive('/') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaHome className="nav-icon" />
                  <span>Home</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/digital-library" 
                  className={`nav-link ${isActive('/digital-library') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaBook className="nav-icon" />
                  <span>Digital Library</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/peer-learning" 
                  className={`nav-link ${isActive('/peer-learning') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaUsers className="nav-icon" />
                  <span>Peer Learning</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/expert-support" 
                  className={`nav-link ${isActive('/expert-support') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaGraduationCap className="nav-icon" />
                  <span>Expert Support</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/exam-preparation" 
                  className={`nav-link ${isActive('/exam-preparation') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaBrain className="nav-icon" />
                  <span>Exam Preparation</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/career" 
                  className={`nav-link ${isActive('/career') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaBriefcase className="nav-icon" />
                  <span>Career</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/qa" 
                  className={`nav-link ${isActive('/qa') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaQuestionCircle className="nav-icon" />
                  <span>Q&A</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/materials" 
                  className={`nav-link ${isActive('/materials') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaBook className="nav-icon" />
                  <span>Materials</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/messaging" 
                  className={`nav-link ${isActive('/messaging') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <div className="nav-icon-wrapper">
                    <FaInbox className="nav-icon" />
                    {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                  </div>
                  <span>Messages</span>
                  {unreadCount > 0 && <span className="notification-text">New</span>}
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/contact" 
                  className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <FaEnvelope className="nav-icon" />
                  <span>Contact</span>
                </Link>
              </li>
            </div>

            <div className="nav-menu-footer">
              {currentUser ? (
                <>
                  <li className="nav-item">
                    <Link 
                      to="/profile" 
                      className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
                      onClick={closeMenu}
                    >
                      <FaUserCog className="nav-icon" />
                      <span>Profile</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link 
                      to="/achievements" 
                      className={`nav-link ${isActive('/achievements') ? 'active' : ''}`}
                      onClick={closeMenu}
                    >
                      <FaTrophy className="nav-icon" />
                      <span>Achievements</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <button 
                      className="nav-link logout-button"
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt className="nav-icon" />
                      <span>Logout</span>
                    </button>
                  </li>
                </>
              ) : (
                <li className="nav-item">
                  <Link 
                    to="/login" 
                    className={`nav-link ${isActive('/login') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <FaUser className="nav-icon" />
                    <span>Login</span>
                  </Link>
                </li>
              )}
            </div>
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
